import { useEffect, useState } from 'react';
import { InfoCard, Progress, StatusAborted, StatusError, StatusOK } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Check, evaluate, Measure, snykRepo, verdict } from './health';

const SNYK_VERSION = '2024-06-21';

const icon = (pass?: boolean) => {
  if (pass === undefined) return <StatusAborted />;
  return pass ? <StatusOK /> : <StatusError />;
};

const TITLE = { pass: 'Nota de salud: aprobado', fail: 'Nota de salud: no pasa', unknown: 'Nota de salud: faltan datos' };

/** Nota de salud del servicio: Sonar (cobertura y calificaciones) y Snyk (críticas y altas). */
export const ServiceHealthCard = () => {
  const { entity } = useEntity();
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [checks, setChecks] = useState<Check[]>();
  const sonarKey = entity.metadata.annotations?.['sonarqube.org/project-key'];
  const snykOrg = entity.metadata.annotations?.['snyk.io/org-id'];
  const slug = entity.metadata.annotations?.['github.com/project-slug'];

  useEffect(() => {
    (async () => {
      const [sonarBase, proxy] = await Promise.all([discovery.getBaseUrl('sonarqube'), discovery.getBaseUrl('proxy')]);
      const json = async (url: string) => {
        const res = await fetch(url);
        return res.ok ? res.json() : undefined;
      };
      const findings = sonarKey ? await json(`${sonarBase}/findings?componentKey=${encodeURIComponent(sonarKey)}`) : undefined;
      let snyk: [number, number] | undefined;
      if (snykOrg && slug) {
        const repo = slug.split('/')[1];
        const page = await json(
          `${proxy}/snyk/rest/orgs/${snykOrg}/projects?version=${SNYK_VERSION}&limit=100&meta.latest_issue_counts=true` +
            `&names_start_with=${encodeURIComponent(slug)}&names_start_with=${encodeURIComponent(repo)}`,
        );
        const projects = (page?.data ?? []).filter((p: any) => snykRepo(String(p.attributes?.name ?? '')) === repo);
        if (projects.length) {
          snyk = projects.reduce(
            ([c, h]: [number, number], p: any) => [c + (p.meta?.latest_issue_counts?.critical ?? 0), h + (p.meta?.latest_issue_counts?.high ?? 0)],
            [0, 0],
          );
        }
      }
      setChecks(evaluate(findings?.measures as Measure[] | undefined, snyk));
    })().catch(() => setChecks(evaluate(undefined, undefined)));
  }, [discovery, fetch, sonarKey, snykOrg, slug]);

  if (!checks) return <InfoCard title="Nota de salud"><Progress /></InfoCard>;
  return (
    <InfoCard title={TITLE[verdict(checks)]} subheader="Cobertura mayor a 90 %, Sonar todo en A y Snyk sin críticas ni altas">
      {checks.map(c => (
        <div key={c.label} style={{ display: 'flex', gap: 8, padding: '4px 0' }}>
          {icon(c.pass)}
          <span>
            <b>{c.label}</b>: {c.detail}
          </span>
        </div>
      ))}
    </InfoCard>
  );
};
